import type { components } from '@/types/api.generated';

/**
 * Closed error-code set (ADR-0006 §5). Mirrors the backend's `ERROR_CODES`
 * in `platform/http/envelope.ts`; the error envelope is
 * `{ error: { code, message, extras } }` where `code` is exactly one of
 * these strings. Derive unions from this map — never inline the literals at
 * a call site (Tier B: no magic-string cross-component contracts).
 */
export const ERROR_CODES = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  METHOD_NOT_ALLOWED: 'METHOD_NOT_ALLOWED',
  CONFLICT: 'CONFLICT',
  LOCKED: 'LOCKED',
  RATE_LIMITED: 'RATE_LIMITED',
  REQUEST_FAILED: 'REQUEST_FAILED',
  INTERNAL: 'INTERNAL',
  ONFLEET_OUTBOUND_FAILED: 'ONFLEET_OUTBOUND_FAILED',
  REPLAY_PAYLOAD_UNAVAILABLE: 'REPLAY_PAYLOAD_UNAVAILABLE',
  MAINTENANCE: 'MAINTENANCE',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/**
 * Compile-time drift guard against the generated OpenAPI contract
 * (`types/api.generated.ts` ← backend `ERROR_CODES`). If the backend adds,
 * renames, or drops a code and regenerates, one direction of this mutual
 * assignment stops compiling until `ERROR_CODES` above is updated — the map
 * cannot silently diverge from the wire contract. This is also the single
 * point where the generated types are wired into the api-client typing.
 */
type GeneratedErrorCode = components['schemas']['ErrorEnvelope']['error']['code'];
type AssertMutualEqual = ErrorCode extends GeneratedErrorCode
  ? GeneratedErrorCode extends ErrorCode
    ? true
    : never
  : never;
const _codesMatchGenerated: AssertMutualEqual = true;
void _codesMatchGenerated;

const ERROR_CODE_VALUES: ReadonlySet<string> = new Set(Object.values(ERROR_CODES));

/** Narrow an unknown wire value to a member of the closed error-code set. */
export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && ERROR_CODE_VALUES.has(value);
}

/**
 * Deterministic closed-set → i18n key. The `error.code.<CODE>` namespace is
 * defined in every non-frozen locale (`en.json` / `zh.json`). Error DISPLAY
 * resolves through this key — never off the human `message`.
 *
 * The template below (`error.code.${code}`) is also what makes the static
 * i18n coverage checker treat every `error.code.*` locale key as used.
 */
export function errorCodeMessageKey(code: ErrorCode): string {
  return `error.code.${code}`;
}
